import { IAddProject, IAddProjectByDataset, IEditProject, ProjectSearchParams } from "@types";
import { client, getErrorMessage } from "./client";
import { buildQueryParams } from "../utils/utils";

const baseUrl = "/projects";

export const getProject = async (id: number, page?: number, size?: number, label?: string, classCount?: string) => {
  try {
    const queryParams = new URLSearchParams();
    // size = 100;
    if (page !== undefined) {
      queryParams.append("page", page.toString());
    }

    if (size !== undefined) {
      queryParams.append("size", size.toString());
    }
    if (label !== undefined) {
      queryParams.append("class", label.toString());
    }

    if (classCount !== undefined) {
      queryParams.append("class", classCount);
    }

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
    const response = await client.get(`/projects/${id}${queryString}`);

    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getProjectLastLabelSeq = async (id: number, label?: string) => {
  try {
    const response = await client.get(`${baseUrl}/${id}/last-label-seq`, {
      params: { label },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getProjectsPagination = async (page: number, size: number) => {
  try {
    const response = await client.get(`${baseUrl}/projects/more?page=${page}&size=${size}`);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const checkProjectName = async (name: string): Promise<boolean> => {
  try {
    await client.get(`${baseUrl}/check-name`, {
      params: { name },
    });

    return false;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};
export const createProject = async (projectData: IAddProject) => {
  try {
    const response = await client.post(`${baseUrl}`, projectData);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const createProjectByDataset = async (projectData: IAddProjectByDataset) => {
  try {
    const response = await client.post(`${baseUrl}/by-dataset`, projectData);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const editProject = async (id: number, projectData: IEditProject) => {
  try {
    const response = await client.patch(`${baseUrl}/${id}`, projectData);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// config 업데이트
export const editProjectConfig = async (id: number, config: Record<string, any>) => {
  try {
    const response = await client.put(`${baseUrl}/${id}/config`, config);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const editImageLabel = async (projectId: number, path: string, label: string) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/label`, {
      path,
      label,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const applyAILabel = async (projectId: number, aiLabel: string) => {
  try {
    const response = await client.patch(`${baseUrl}/${projectId}/labels/apply-ai`, {
      aiLabel,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getImageLabelsForValid = async (id: number, label?: string) => {
  try {
    const response = await client.get(`${baseUrl}/${id}/valid-count`, {
      params: { label },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const autoEditImageLabel = async (projectId: number, label: string) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/auto-label`, {
      label,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const deleteImageLabels = async (imageIds: string[]) => {
  try {
    const response = await client.post(`${baseUrl}/delete-labels`, {
      imageIds,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 사전분류된 이미지들을 선택하여 여러 이미지의 라벨 변경
export const editPreClassLabel = async (projectId: number, images: number[], label: string) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/labels`, {
      images,
      label,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const putDatasetImages = async (projectId: number, images: string[]) => {
  try {
    const response = await client.put(`${baseUrl}/${projectId}/images`, images);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const patchDatasetImages = async (projectId: number, addedImages: string[]) => {
  try {
    const response = await client.patch(`${baseUrl}/${projectId}/images`, {
      addedImages,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// export const deleteProject = async (id: number) => {
//   try {
//     const response = await client.delete(`${baseUrl}/${id}`);
//     return response.data;
//   } catch (error: any) {
//     throw new Error(getErrorMessage(error));
//   }
// };

export const updateJsonFile = async (id: number) => {
  try {
    const response = await client.get(`${baseUrl}/${id}/dataset-update`);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const startTrainProject = async (id: number, step: string, label?: string, multi?: boolean, epoch?: number) => {
  try {
    const response = await client.post(`${baseUrl}/${id}/train`, {
      server: "192.168.2.29:50051",
      step: step,
      class: label,
      multi,
      epoch,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getLabelHistory = async (id: number) => {
  try {
    const response = await client.get(`labelHistory/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 분석 프로젝트 class값 전달
export const postPreClass = async (projectId: number, classCount: number) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/pre-class`, {
      classCount,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const updatePreClassLabel = async (projectId: number, images: any[]) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/pre-class`, {
      images: images,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 이미지 정렬
export const getImageSort = async (projectId: number) => {
  try {
    const response = await client.get(`${baseUrl}/${projectId}/images/sort`);
    return response.data;
  } catch (error: any) {
    // throw new Error(getErrorMessage(error));
    console.error(error);
  }
};

// 클래스 추가
export const addProjectClass = async (projectId: number, classItem: string) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/class`, {
      class: classItem,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 클래스 한번에 배열로 전달하여 추가
export const addPreClassLabels = async (projectId: number, classItems: string[]) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/classes`, {
      classes: classItems,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 클래스 수정
export const editProjectClass = async (projectId: number, updatedIndex: number, newLabel: string) => {
  try {
    const response = await client.patch(`${baseUrl}/${projectId}/class`, {
      updateIndex: updatedIndex,
      class: newLabel,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 특정 클래스 삭제
export const deleteProjectClass = async (projectId: number, classItem: string) => {
  try {
    const response = await client.delete(`${baseUrl}/${projectId}/class?class=${classItem}`);
    console.log(response);
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 학습 히스토리
interface GetTrainHistoriesParams {
  id: number;
  label?: string;
  iteration?: number;
  sortOption?: string;
  filterOption?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    accuracy?: number;
  };
  page?: number;
  size?: number;
  start_date?: string;
  end_date?: string;
}

export const getTrainHistories = async ({
  id,
  label,
  iteration,
  sortOption,
  filterOption,
  page,
  size,
  start_date,
  end_date,
}: GetTrainHistoriesParams) => {
  try {
    const queryObject: Record<string, any> = {
      page,
      size,
      class: label,
      iteration,
      start_date,
      end_date,
      sort: sortOption,
      ...filterOption, // startDate, endDate, status, accuracy 등 포함
    };

    const queryString = buildQueryParams(queryObject);
    const response = await client.get(`${baseUrl}/${id}/train-history${queryString ? `?${queryString}` : ""}`);

    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const getLatestTrainHistory = async (id: number) => {
  try {
    const response = await client.get(`/trainHistory/latest/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const updateTrainHistory = async () => {};

// 프로젝트 config 업데이트
export const updateDatasetFile = async (projectId: number) => {
  try {
    const response = await client.get(`${baseUrl}/${projectId}/dataset-update`);
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

export const excludeFromTrain = async (projectId: number, paths: string[]) => {
  try {
    const response = await client.post(`${baseUrl}/${projectId}/dataset/exclude-train`, { paths });
    return response.data;
  } catch (error: any) {
    throw new Error(getErrorMessage(error));
  }
};

// 프로젝트 NG 데이터 조회
export const getProjectNG = async (id: number) => {
  try {
    const response = await client.get(`${baseUrl}/${id}/ng`);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(getErrorMessage(error));
  }
};

export const getImagesByClass = async (id: number) => {
  try {
    const response = await client.get(`${baseUrl}/${id}/class-images`);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(getErrorMessage(error));
  }
};

export const getProjectGeneralInfo = async (id: number) => {
  try {
    const response = await client.get(`${baseUrl}/${id}/general`);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(getErrorMessage(error));
  }
};

export const getImageContour = async (id: number, image_id: number) => {
  try {
    const response = await client.get(`${baseUrl}/${id}/contours?image=${image_id}`);
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(getErrorMessage(error));
  }
};

export const updateImageContour = async (id: number, image_id: number, contours: string) => {
  try {
    const response = await client.patch(`${baseUrl}/${id}/contours`, {
      image_id,
      contours,
    });
    return response.data;
  } catch (error: any) {
    console.error(error);
    throw new Error(getErrorMessage(error));
  }
};
